import React, { useEffect, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import Avatar from './Avatar';
import { formatDate } from '../utils/format';
import '../../../styles/parentPortal.css';

export default function StudentHeader({ student, onUnlinkClick, showMenu = true }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const firstItemRef = useRef(null);
  const linkedOn = student.linkedAt ? formatDate(student.linkedAt) : null;

  const closeMenu = ({ restoreFocus = false } = {}) => {
    setMenuOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  };

  // Replaces a fragile onBlur+setTimeout close, which raced against Tab's
  // focus move: the trigger's blur fired the instant focus started leaving
  // it, closing the menu before Tab could ever land on the item inside it —
  // keyboard users could never reach "Unlink student". Click-outside +
  // Escape is the correct pattern for a small popover like this.
  useEffect(() => {
    if (!menuOpen) return undefined;

    // Move focus into the menu the moment it opens — previously nothing did,
    // so a keyboard user who opened it via Enter/Space still had to Tab in
    // (which then hit the bug above).
    firstItemRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeMenu({ restoreFocus: true });
    };
    const onMouseDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && !triggerRef.current?.contains(e.target)) {
        closeMenu();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuOpen]);

  return (
    <div
      className="pp-card"
      style={{
        position: 'relative',
        padding: 'var(--space-5)',
        display: 'flex',
        gap: 'var(--space-4)',
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      <Avatar name={student.displayName} color={student.avatarColor} size={64} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 'var(--text-page-size)',
            fontWeight: 'var(--text-page-weight)',
            color: 'var(--text-primary)',
            letterSpacing: 'var(--text-page-tracking)',
          }}
        >
          {student.displayName}
        </h1>
        {student.email && (
          <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginTop: 'var(--space-1)' }}>
            {student.email}
          </div>
        )}
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-4)',
            marginTop: 'var(--space-3)',
            flexWrap: 'wrap',
            fontSize: 'var(--text-body-size)',
            color: 'var(--text-secondary)',
          }}
        >
          {linkedOn && <HeaderStat label="Linked" value={linkedOn} />}
          <HeaderStat
            label="Streak"
            value={`${student.currentStreakDays ?? 0}-day`}
            accent={(student.currentStreakDays ?? 0) > 0}
          />
          <HeaderStat label="Lessons" value={student.totalLessonsCompleted ?? 0} />
        </div>
      </div>

      {showMenu && onUnlinkClick && (
        <div style={{ position: 'relative', alignSelf: 'flex-start' }}>
          <button
            ref={triggerRef}
            className="pp-icon-btn"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="More actions"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            style={{
              width: 36,
              height: 36,
              borderRadius: 'var(--radius-control)',
              border: '1px solid var(--border-subtle)',
              background: 'transparent',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <MoreHorizontal size={18} strokeWidth={1.5} />
          </button>
          {menuOpen && (
            <div
              ref={menuRef}
              role="menu"
              style={{
                position: 'absolute',
                top: 'calc(100% + 6px)',
                right: 0,
                minWidth: 180,
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-control)',
                boxShadow: 'var(--shadow-md)',
                padding: 'var(--space-1)',
                zIndex: 20,
              }}
            >
              <button
                ref={firstItemRef}
                role="menuitem"
                onClick={() => {
                  closeMenu();
                  onUnlinkClick();
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 6,
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--danger)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-tertiary)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                Unlink student
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HeaderStat({ label, value, accent }) {
  return (
    <div>
      <div
        style={{
          fontSize: 'var(--text-label-size)',
          fontWeight: 'var(--text-label-weight)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--text-label-tracking)',
          color: 'var(--text-tertiary)',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 700,
          color: accent ? 'var(--warning)' : 'var(--text-primary)',
          marginTop: 2,
        }}
      >
        {value}
      </div>
    </div>
  );
}
